import type { SessionState } from '@chapsi/contracts';

/**
 * Two-factor sign-in in the web app (docs/adr/0033-two-factor-and-security-alerts.md). Where a session state
 * sends the person next: the second-factor step, the setup a workspace requires, or the app.
 */
export function nextRoute(state: SessionState): '/auth/two-factor' | '/auth/set-up-two-factor' | '/account-deletion' | '/auth/sso-required' | '/' | '/welcome' {
  if (state.secondFactor.status === 'required') return '/auth/two-factor';
  // The account is scheduled for deletion (docs/adr/0040): that screen, with Cancel deletion, is the whole app.
  if (state.accountDeletion) return '/account-deletion';
  // The workspace requires single sign-on and this device didn't come through it (docs/adr/0055).
  if (state.ssoRequired) return '/auth/sso-required';
  if (state.secondFactor.setupRequired) return '/auth/set-up-two-factor';
  return state.currentWorkspaceId ? '/' : '/welcome';
}

/** Saves the recovery codes as a plain text file. Nothing leaves the browser. */
export function downloadRecoveryCodes(codes: string[], email: string): void {
  const text = [
    'Chapsi recovery codes',
    `Account: ${email}`,
    `Made: ${new Date().toLocaleString('en-AU')}`,
    '',
    'Each code works once. Use one when you cannot use your passkey or authenticator app.',
    'Keep them somewhere safe, like a password manager.',
    '',
    ...codes,
    '',
  ].join('\n');
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'chapsi-recovery-codes.txt';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** The QR code for an otpauth:// URI, drawn in the browser (the secret never goes to another service). */
export async function qrSvg(uri: string): Promise<string> {
  const { toString } = await import('qrcode');
  return toString(uri, { type: 'svg', margin: 1, errorCorrectionLevel: 'M', color: { dark: '#0F172A', light: '#FFFFFF' } });
}
